import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { BibleBook, BiblePassage, BibleTranslation, SavedPassage } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { BibleTabs, ChapterAtlasCard } from './Atlas';
import { ChapterDictionaryCard } from './Dictionary';
import { OriginalText } from './Original';
import { Commentary } from './Commentary';
import { HighlightsCard, VerseToolbar } from './Study';

export const CATEGORIES: [NonNullable<SavedPassage['category']>, string][] = [
  ['promesa', 'Promesa'], ['ensenanza', 'Enseñanza'], ['oracion', 'Oración'], ['importante', 'Importante'], ['estudio', 'Estudio'],
];
const categoryLabel = (c: SavedPassage['category']) => CATEGORIES.find(([k]) => k === c)?.[1] ?? null;

const DEFAULT_REF = 'Juan 3';
export const bibleLink = (ref: string) => `/app/biblia?ref=${encodeURIComponent(ref)}`;

/** Bíblico (Motor 13): leer por cita o por libro y capítulo, y guardar pasajes para uno mismo. */
export function BiblePage() {
  const [params, setParams] = useSearchParams();
  const ref = params.get('ref') ?? DEFAULT_REF;
  const translation = params.get('traduccion') ?? '';
  const [savedVersion, setSavedVersion] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);
  const [extrasOpen, setExtrasOpen] = useState(false);
  const words = params.get('buscar');
  const translations = useAsync((signal) => api.get<{ items: BibleTranslation[] }>('/bible/translations', signal).then((r) => r.items), []);
  const books = useAsync((signal) => api.get<{ items: BibleBook[] }>('/bible/books', signal).then((r) => r.items), []);
  const passage = useAsync(
    (signal) => api.get<BiblePassage>(`/bible/passage?ref=${encodeURIComponent(ref)}${translation ? `&translation=${translation}` : ''}`, signal),
    [ref, translation],
  );
  const go = (next: string) => setParams(translation ? { ref: next, traduccion: translation } : { ref: next });

  function search(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const text = String(new FormData(event.currentTarget).get('cita') ?? '').trim();
    if (text) go(text);
  }

  const current = passage.status === 'ready' ? passage.data : null;
  const book = current && books.status === 'ready' ? books.data.find((b) => b.code === current.reference.book) : undefined;

  return (
    <div className="page page--narrow bible-wide">
      <header className="page__header">
        <h1>Biblia</h1>
      </header>
      <BibleTabs active="leer" />
      {/* En escritorio: buscador fijo a la izquierda y el texto con el resto del ancho. */}
      <div className="bible-layout">
      <aside className="bible-layout__side">
      <Card>
        <form className="bible__search" onSubmit={search} role="search">
          <Field label="Buscar una cita" name="cita" key={ref} defaultValue={ref} placeholder="Juan 3:16, Sal 23, 1 Co 13" autoComplete="off" />
          <Button type="submit">Leer</Button>
        </form>
        {/* En el celular, libro, capítulo, traducción y búsqueda de palabras quedan detrás de «Más opciones». */}
        <button type="button" className="link-button only-mobile bible__more-toggle" aria-expanded={moreOpen} aria-controls="bible-more" onClick={() => setMoreOpen((o) => !o)}>
          {moreOpen ? 'Menos opciones' : 'Más opciones'}
        </button>
        <div id="bible-more" className={`bible__more${moreOpen || words ? ' bible__more--open' : ''}`}>
        {books.status === 'ready' && current ? (
          <div className="grid-2">
            <div className="field">
              <label htmlFor="bible-book">Libro</label>
              <select id="bible-book" value={current.reference.book} onChange={(e) => go(`${books.data.find((b) => b.code === e.target.value)!.name} 1`)}>
                {books.data.map((b) => (
                  <option key={b.code} value={b.code}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="bible-chapter">Capítulo</label>
              <select id="bible-chapter" value={current.reference.chapter} onChange={(e) => go(`${book!.name} ${e.target.value}`)}>
                {Array.from({ length: book?.chapters ?? 1 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : null}
        <form
          className="bible__search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            const q = String(new FormData(e.currentTarget).get('palabra') ?? '').trim();
            if (q.length >= 2) setParams({ ref, buscar: q });
          }}
        >
          <Field label="Buscar una palabra o frase" name="palabra" defaultValue={words ?? ''} placeholder="amor, fe, no temas" autoComplete="off" />
          <Button type="submit" variant="secondary">Buscar</Button>
        </form>
        {translations.status === 'ready' && translations.data.length > 1 ? (
          <div className="field">
            <label htmlFor="bible-translation">Traducción</label>
            <select id="bible-translation" value={translation || current?.translation?.code || ''} onChange={(e) => setParams({ ref, traduccion: e.target.value })}>
              {translations.data.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        </div>
      </Card>
      <div className={`bible-layout__extras${extrasOpen ? '' : ' bible-layout__extras--folded'}`}>
        <button type="button" className="btn btn--secondary only-mobile bible__extras-toggle" aria-expanded={extrasOpen} onClick={() => setExtrasOpen((o) => !o)}>
          {extrasOpen ? 'Ocultar resaltados y notas' : 'Mis resaltados, notas y compartidas'}
        </button>
        <HighlightsCard />
        <SavedCard version={savedVersion} />
        <SharedCard />
      </div>
      </aside>
      <div className="bible-layout__main">
      {words ? <WordSearch q={words} translation={translation} onClose={() => setParams({ ref })} /> : null}
      {passage.status === 'loading' ? <Loading /> : null}
      {passage.status === 'error' ? <ErrorState error={passage.error} /> : null}
      {current ? <PassageCard passage={current} onNavigate={go} onSaved={() => setSavedVersion((v) => v + 1)} /> : null}
      {current && current.verses.length ? (
        <StudyChapterCard key={current.label} passage={current} translation={translation} canCompare={translations.status === 'ready' && translations.data.length > 1} />
      ) : null}
      </div>
      </div>
    </div>
  );
}

type StudyTool = 'comentario' | 'original' | 'atlas' | 'diccionario' | 'relacionados' | 'comparar';

/** «Estudiar este capítulo»: todas las ayudas de estudio en una sola tarjeta; cada botón abre la suya ahí mismo. */
function StudyChapterCard({ passage: p, translation, canCompare }: { passage: BiblePassage; translation: string; canCompare: boolean }) {
  const [tool, setTool] = useState<StudyTool | null>(null);
  const { book, chapter, verseStart, verseEnd } = p.reference;
  const close = <Button variant="ghost" onClick={() => setTool(null)}>Cerrar</Button>;
  const tools: [StudyTool, string][] = [
    ['comentario', 'Comentario'],
    ['original', 'Hebreo/griego'],
    ['atlas', 'Personajes y lugares'],
    ['diccionario', 'Diccionario'],
    ...(verseStart !== null ? [['relacionados', 'Textos relacionados'] as [StudyTool, string]] : []),
    ...(canCompare ? [['comparar', 'Comparar versiones'] as [StudyTool, string]] : []),
  ];
  return (
    <section className="card study-chapter">
      <header className="card__header">
        <h2 className="card__title">Estudiar este capítulo</h2>
      </header>
      <div className="chips" role="group" aria-label="Ayudas de estudio">
        {tools.map(([key, label]) => (
          <button key={key} type="button" className={`chip${tool === key ? ' chip--active' : ''}`} aria-pressed={tool === key} onClick={() => setTool(tool === key ? null : key)}>
            {label}
          </button>
        ))}
      </div>
      {tool === 'comentario' ? <Commentary book={book} chapter={chapter} onClose={() => setTool(null)} /> : null}
      {tool === 'original' ? <OriginalText book={book} chapter={chapter} verseStart={verseStart} verseEnd={verseEnd} onClose={() => setTool(null)} /> : null}
      {tool === 'atlas' ? <ChapterAtlasCard book={book} chapter={chapter} action={close} /> : null}
      {tool === 'diccionario' ? <ChapterDictionaryCard book={book} chapter={chapter} action={close} /> : null}
      {tool === 'relacionados' ? <CrossRefsCard reference={p.label} translation={translation} action={close} /> : null}
      {tool === 'comparar' ? <CompareCard reference={p.label} onClose={() => setTool(null)} /> : null}
    </section>
  );
}

function PassageCard({ passage: p, onNavigate, onSaved }: { passage: BiblePassage; onNavigate: (ref: string) => void; onSaved: () => void }) {
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const partial = p.reference.verseStart !== null;
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const highlights = useAsync(
    (signal) =>
      p.verses.length
        ? api.get<{ items: { verse: number; color: string }[] }>(`/bible/me/highlights/chapter?book=${p.reference.book}&chapter=${p.reference.chapter}`, signal).then((r) => r.items)
        : Promise.resolve([]),
    [p.reference.book, p.reference.chapter, p.verses.length],
  );
  const colors = new Map((highlights.status === 'ready' ? highlights.data : []).map((h) => [h.verse, h.color]));
  const toggle = (verse: number) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(verse)) next.delete(verse);
      else next.add(verse);
      return next;
    });
  const chapterLabel = p.label.replace(/:.*$/, '');

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const f = new FormData(event.currentTarget);
      await api.post('/bible/me/saved', { ref: p.label, note: String(f.get('note') ?? '') || null, category: String(f.get('category') ?? '') || null });
      setSaving(false);
      setNotice(`${p.label} guardado.`);
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={p.label}>
      {p.translation === null ? (
        <Alert tone="info">Aún no hay una traducción de la Biblia cargada en este servidor. La administración del servidor puede importarla.</Alert>
      ) : p.verses.length === 0 ? (
        <p className="muted">Este pasaje no está en la traducción cargada ({p.translation.name}).</p>
      ) : (
        <div className="reading bible__text">
          <p>
            {p.verses.map((v) => (
              <span
                key={v.verse}
                role="button"
                tabIndex={0}
                aria-pressed={selected.has(v.verse)}
                className={`verse${colors.get(v.verse) ? ` hl hl--${colors.get(v.verse)}` : ''}${selected.has(v.verse) ? ' verse--selected' : ''}`}
                onClick={() => toggle(v.verse)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), toggle(v.verse))}
              >
                <sup>{v.verse}</sup> {v.text}{' '}
              </span>
            ))}
          </p>
        </div>
      )}
      {selected.size > 0 ? (
        <VerseToolbar
          passage={p}
          verses={[...selected].sort((a, b) => a - b)}
          onDone={(m) => {
            setSelected(new Set());
            if (m) setNotice(m);
            highlights.reload();
          }}
        />
      ) : p.verses.length > 0 ? (
        <p className="muted small">Toca un versículo para resaltarlo, memorizarlo o crear una imagen.</p>
      ) : null}
      {p.translation ? <p className="muted small">{`${p.translation.name}. ${p.translation.license}`}</p> : null}
      {notice ? <Alert tone="success">{notice}</Alert> : null}
      {saving ? (
        <form className="form" onSubmit={save} noValidate>
          <div className="field">
            <label htmlFor="bible-note">Nota (opcional)</label>
            <textarea id="bible-note" name="note" rows={3} maxLength={4000} />
          </div>
          <div className="field">
            <label htmlFor="bible-category">Categoría</label>
            <select id="bible-category" name="category" defaultValue="">
              <option value="">Sin categoría</option>
              {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          {error ? <Alert>{error}</Alert> : null}
          <div className="actions">
            <Button type="submit" busy={busy}>
              Guardar {p.label}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setSaving(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : (
        <div className="actions actions--wrap">
          {p.previous ? (
            <Button variant="ghost" onClick={() => onNavigate(p.previous!)} aria-label={`Anterior: ${p.previous}`}>
              ← {p.previous}
            </Button>
          ) : null}
          {partial ? (
            <Button variant="ghost" onClick={() => onNavigate(chapterLabel)}>
              Capítulo completo
            </Button>
          ) : null}
          {p.next ? (
            <Button variant="ghost" onClick={() => onNavigate(p.next!)} aria-label={`Siguiente: ${p.next}`}>
              {p.next} →
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => (setNotice(null), setSaving(true))}>
            Guardar pasaje
          </Button>
        </div>
      )}
    </Card>
  );
}

type ShareOptions = { family: boolean; cells: { id: string; name: string }[]; ministries: { id: string; name: string }[] };
type Shared = SavedPassage & { author: string; audience: string };

/** Mis notas y pasajes guardados: privados hasta que decido compartirlos. También en el Cuaderno («Notas de la Biblia»). */
export function SavedCard({ version, title = 'Mis notas y pasajes' }: { version: number; title?: string }) {
  const me = useMe();
  const saved = useAsync((signal) => api.get<{ items: SavedPassage[] }>('/bible/me/saved', signal).then((r) => r.items), [version]);
  const options = useAsync(
    (signal) => (me.activeChurchId ? api.get<{ options: ShareOptions }>(`/bible/churches/${me.activeChurchId}/shared`, signal).then((r) => r.options) : Promise.resolve(null)),
    [me.activeChurchId],
  );
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function remove(id: string) {
    await api.delete(`/bible/me/saved/${id}`);
    setRemoved((s) => new Set(s).add(id));
  }

  if (saved.status !== 'ready') return null;
  const items = saved.data.filter((s) => !removed.has(s.id) && (!filter || s.category === filter));
  const opts = options.status === 'ready' ? options.data : null;
  const shareLabel = (s: SavedPassage) =>
    !s.share ? 'Privada' : s.share.scope === 'familia' ? 'Con mi familia' : `Con ${(s.share.scope === 'celula' ? opts?.cells : opts?.ministries)?.find((x) => x.id === s.share!.target)?.name ?? (s.share.scope === 'celula' ? 'mi célula' : 'mi ministerio')}`;
  return (
    <Card title={title}>
      <div className="field">
        <label htmlFor="notes-filter">Categoría</label>
        <select id="notes-filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">Todas</option>
          {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      {error ? <Alert>{error}</Alert> : null}
      {items.length === 0 ? <EmptyState title="Aún no guardas pasajes." /> : null}
      <ul className="list">
        {items.map((s) => (
          <li key={s.id} className="list__item list__item--stack">
            <span>
              <Link to={bibleLink(s.label)}><strong>{s.label}</strong></Link>
              {s.category ? <span className={`badge note-${s.category}`}> {categoryLabel(s.category)}</span> : null}
              <span className="small muted"> · {shareLabel(s)}</span>
              {s.note ? <span className="pre-line small"><br />{s.note}</span> : null}
            </span>
            {editing === s.id ? (
              <form
                className="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  const f = new FormData(e.currentTarget);
                  const share = String(f.get('share') ?? '');
                  const [scope, target] = share.split(':');
                  setError(null);
                  api
                    .put(`/bible/me/saved/${s.id}`, { note: String(f.get('note') ?? '') || null, category: String(f.get('category') ?? '') || null, share: scope ? { scope, target: target || null } : null })
                    .then(() => { setEditing(null); saved.reload(); })
                    .catch((err) => setError(errorMessage(err)));
                }}
              >
                <div className="field">
                  <label htmlFor={`note-${s.id}`}>Nota</label>
                  <textarea id={`note-${s.id}`} name="note" rows={3} maxLength={4000} defaultValue={s.note ?? ''} />
                </div>
                <div className="form__row">
                  <div className="field">
                    <label htmlFor={`cat-${s.id}`}>Categoría</label>
                    <select id={`cat-${s.id}`} name="category" defaultValue={s.category ?? ''}>
                      <option value="">Sin categoría</option>
                      {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </div>
                  <div className="field">
                    <label htmlFor={`share-${s.id}`}>¿Quién la ve?</label>
                    <select id={`share-${s.id}`} name="share" defaultValue={s.share ? `${s.share.scope}:${s.share.target ?? ''}` : ''}>
                      <option value="">Solo yo</option>
                      {opts?.family ? <option value="familia:">Mi familia</option> : null}
                      {opts?.cells.map((c) => <option key={c.id} value={`celula:${c.id}`}>Célula {c.name}</option>)}
                      {opts?.ministries.map((m) => <option key={m.id} value={`ministerio:${m.id}`}>{m.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="actions">
                  <Button type="submit">Guardar</Button>
                  <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
                </div>
              </form>
            ) : (
              <div className="actions">
                <Button variant="ghost" onClick={() => setEditing(s.id)}>Editar</Button>
                <Button variant="ghost" onClick={() => remove(s.id)} aria-label={`Quitar ${s.label}`}>Quitar</Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Notas que mi familia, mis células o mis ministerios compartieron conmigo. */
function SharedCard() {
  const me = useMe();
  const shared = useAsync(
    (signal) => (me.activeChurchId ? api.get<{ items: Shared[] }>(`/bible/churches/${me.activeChurchId}/shared`, signal).then((r) => r.items) : Promise.resolve([])),
    [me.activeChurchId],
  );
  if (shared.status !== 'ready' || shared.data.length === 0) return null;
  return (
    <Card title="Compartidas conmigo">
      <ul className="list">
        {shared.data.map((s) => (
          <li key={s.id} className="list__item list__item--stack">
            <span>
              <Link to={bibleLink(s.label)}><strong>{s.label}</strong></Link>
              {s.category ? <span className={`badge note-${s.category}`}> {categoryLabel(s.category)}</span> : null}
              <span className="small muted"> · {s.author} · {s.audience}</span>
            </span>
            {s.note ? <span className="pre-line small">{s.note}</span> : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function WordSearch({ q, translation, onClose }: { q: string; translation: string; onClose: () => void }) {
  const found = useAsync((signal) => api.get<{ items: { label: string; ref: string; text: string }[] }>(`/bible/search?q=${encodeURIComponent(q)}${translation ? `&translation=${translation}` : ''}`, signal).then((r) => r.items), [q, translation]);
  return (
    <Card title={`Resultados para «${q}»`} action={<Button variant="ghost" onClick={onClose}>Cerrar</Button>}>
      {found.status === 'loading' ? <Loading /> : null}
      {found.status === 'error' ? <ErrorState error={found.error} /> : null}
      {found.status === 'ready' ? (
        found.data.length === 0 ? <p className="muted small">No encontré esa palabra.</p> : (
          <>
            <p className="small muted">{found.data.length === 100 ? 'Primeros 100 resultados.' : `${found.data.length} resultados.`}</p>
            <ul className="list">
              {found.data.map((r) => (
                <li key={r.ref} className="list__item list__item--stack">
                  <Link to={bibleLink(r.ref)}><strong>{r.label}</strong></Link>
                  <span className="small">{r.text}</span>
                </li>
              ))}
            </ul>
          </>
        )
      ) : null}
    </Card>
  );
}

function CompareCard({ reference, onClose }: { reference: string; onClose: () => void }) {
  const data = useAsync((signal) => api.get<{ items: BiblePassage[] }>(`/bible/compare?ref=${encodeURIComponent(reference)}`, signal).then((r) => r.items), [reference]);
  return (
    <Card title={`Comparar ${reference}`} action={<Button variant="ghost" onClick={onClose}>Cerrar</Button>}>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'ready' ? (
        <div className="bible__compare">
          {data.data.map((p) => (
            <section key={p.translation?.code}>
              <h3 className="small">{p.translation?.name}</h3>
              <p className="bible__text">{p.verses.map((v) => <span key={v.verse}><sup>{v.verse}</sup> {v.text} </span>)}</p>
            </section>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

/** Referencias cruzadas: textos relacionados (OpenBible.info, CC-BY). */
function CrossRefsCard({ reference, translation, action }: { reference: string; translation: string; action?: ReactNode }) {
  const data = useAsync((signal) => api.get<{ items: { label: string; ref: string; text: string | null; votes: number }[]; attribution: string }>(`/bible/crossrefs?ref=${encodeURIComponent(reference)}${translation ? `&translation=${translation}` : ''}`, signal), [reference, translation]);
  if (data.status === 'loading') return <Card title="Textos relacionados" action={action}><Loading /></Card>;
  if (data.status === 'error') return <Card title="Textos relacionados" action={action}><ErrorState error={data.error} /></Card>;
  if (data.data.items.length === 0) return <Card title="Textos relacionados" action={action}><p className="muted small">No hay textos relacionados para esta cita.</p></Card>;
  return (
    <Card title="Textos relacionados" action={action}>
      <ul className="list">
        {data.data.items.slice(0, 12).map((r) => (
          <li key={r.ref} className="list__item list__item--stack">
            <Link to={bibleLink(r.ref)}><strong>{r.label}</strong></Link>
            {r.text ? <span className="small">{r.text.length > 220 ? `${r.text.slice(0, 220)}…` : r.text}</span> : null}
          </li>
        ))}
      </ul>
      <p className="small muted">{data.data.attribution}</p>
    </Card>
  );
}

